import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  appliedPlaylistRemovals,
  authPlaylistReadPreferred,
  DEFAULT_SEARCH_QUERY,
  formatRoute,
  playlistCountAfterRemovals,
  playlistWalk,
  PLAYLIST_FIRST_PAGE,
  PLAYLIST_HEADER_PROBE_PAGE,
  rangeToggle,
  withoutPlaylistEntries,
  type Playlist,
  type PlaylistPrivacy,
} from '@visious/core';
import { fetchAuthPlaylistPage, fetchPlaylistPage } from '../../api/playlists';
import { fetchSearch } from '../../api/search';
import { fetchVideoDetails } from '../../api/videos';
import type { AuthApiProblem } from '../../api/auth';
import type { ApiProblem } from '../../api/videos';
import { describeAuthProblem, type ProblemCopy } from '../../api/problem';
import { useWatchProgressLookup } from '../../history/useWatchProgress';
import { systemClock } from '../../platform/clock';
import { getPlatform } from '../../platform/instance';
import { navigate } from '../../router/router';
import { useSession } from '../../platform/session';
import { describeWriteProblem, ownedPlaylistsProblem } from '../../playlists/model';
import { useEditPlaylist, useEntryMutations } from '../../playlists/mutations';
import { setPlaylistReversed, usePlaylistReversed } from '../../playlists/orderMemory';
import {
  forgetPlaylistPrivacy,
  rememberPlaylistPrivacy,
  useKnownPlaylistPrivacy,
} from '../../playlists/privacyMemory';
import { authPlaylistKey, useOwnedPlaylists } from '../../playlists/useOwnedPlaylists';
import { fmt, text } from '../../strings';
import {
  classifyAddVideoInput,
  playlistScreenState,
  videoCandidateFromDetails,
  videoCandidatesFromSearch,
  type PlaylistScreenState,
  type VideoCandidateRow,
} from './model';

/**
 * The React binding for §8's playlist page: one query, the clock, and — since 3c — the four writes that
 * apply to a playlist of your own.
 *
 * **`useInfiniteQuery`, for `useChannel.ts`'s reasons**, with the difference 2e named: the accumulator is
 * not a concatenation. The pages overlap, so `playlistWalk` deduplicates by `index`, and
 * `getNextPageParam` cannot read the next cursor off the last page alone. That holds for the
 * authenticated endpoint too, verified rather than assumed — on a 120-entry native playlist both
 * endpoints answered byte-identically, page 1 = index 0..99 and page 2 = index 50..119, so the stride is
 * 50 where the mirrored endpoint's is ~100 and the same rule terminates both.
 *
 * **Reversed, the page walk turns around with the display.** Showing the last entry first while
 * fetching from the first entry forwards would mean reading a 7 271-entry playlist end to end before
 * its top row could be drawn, so the walk opens on the header-only probe page, names the page holding
 * the last entry from the count it carries, and steps down. Both directions share one cache, and
 * `playlistWalk` is what keeps that safe: what the reader sees is the contiguous run from the end
 * being read, so a playlist paged forwards and then reversed withholds page 1's rows until the walk
 * back has reached them, instead of drawing them under the last page as though nothing were missing.
 *
 * `retry: false` for 1c's reason: §3's retry and backoff live in
 * `createApiTransport`.
 */
export interface PlaylistBinding {
  readonly state: PlaylistScreenState;
  readonly loadMore: () => void;
  readonly retry: () => void;
  readonly refreshing: boolean;
  readonly edit: PlaylistEditBinding;
  readonly manage: PlaylistManageBinding;
  /** The display order, per playlist and per browser (`playlists/orderMemory.ts`). */
  readonly reversed: boolean;
  readonly setReversed: (reversed: boolean) => void;
  /** The grid search — display-only, so it is state here and never in the URL. */
  readonly search: string;
  readonly setSearch: (value: string) => void;
  /* — the browse-mode per-entry remove, the history page's shape — */
  readonly removeEntry: (indexId: string) => void;
  readonly removingIndexIds: ReadonlySet<string>;
  /** A refused single remove, shown by the page rather than inside a manage panel that is not open. */
  readonly entryProblem: ProblemCopy | null;
}

/** What the manage mode's add box is showing — the manage-subscriptions page's `FindState`, for videos. */
export type VideoFindState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'results'; readonly rows: readonly VideoCandidateRow[] }
  | { readonly kind: 'nothing'; readonly q: string }
  /** A pasted link or id whose video YouTube will not serve — there is nothing to add. */
  | { readonly kind: 'not-available' }
  | { readonly kind: 'problem'; readonly detail: string };

/** One playlist the selection can be copied or moved into. */
export interface TransferTarget {
  readonly playlistId: string;
  readonly title: string;
  readonly countText: string | null;
}

/**
 * Manage mode. Component state rather than the URL, for the same reason the Playlists page's
 * is: a selection is not addressable, not shareable, and Back must not "undo" it.
 */
export interface PlaylistManageBinding {
  readonly active: boolean;
  readonly toggle: () => void;
  readonly selectedCount: number;
  /** `range` is a held Shift (2026-08-02): the previously toggled entry to this one, inclusive. */
  readonly toggleSelect: (indexId: string, range: boolean) => void;
  readonly clearSelection: () => void;
  /** `null` while nothing runs; otherwise progress for the manage bar's own words. */
  readonly working: { readonly done: number; readonly total: number } | null;
  readonly problem: ProblemCopy | null;
  /* — the add box — */
  readonly addInput: string;
  readonly setAddInput: (value: string) => void;
  readonly find: () => void;
  readonly findState: VideoFindState;
  readonly addVideo: (videoId: string) => void;
  /** Per candidate row, keyed by video id — `entries.busy` keys adds by *playlist*, too coarse here. */
  readonly addStates: ReadonlyMap<string, 'adding' | 'added' | 'failed'>;
  /* — the three acts over the selection — */
  readonly removeSelected: () => void;
  /** Which transfer dialog is open, if any. */
  readonly transfer: 'copy' | 'move' | null;
  readonly openTransfer: (kind: 'copy' | 'move') => void;
  readonly closeTransfer: () => void;
  /** `null` while the owned list loads — it is fetched when the dialog opens, never on mount. */
  readonly targets: readonly TransferTarget[] | null;
  readonly targetsProblem: ProblemCopy | null;
  readonly transferTo: (playlistId: string) => void;
}

/**
 * The editing half. It is a *panel*, not a route: nothing about "I am renaming this" belongs in the URL,
 * and Back must not mean "undo half a typed title" (2c's rule holds precisely because it is about
 * filters, which are addressable, and this is not).
 */
export interface PlaylistEditBinding {
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly title: string;
  readonly setTitle: (title: string) => void;
  readonly description: string;
  readonly setDescription: (description: string) => void;
  /** `null` while the API's own answer stands — see `describeVisibility` for why it may not be known. */
  readonly privacy: PlaylistPrivacy | null;
  readonly setPrivacy: (privacy: PlaylistPrivacy) => void;
  readonly save: () => void;
  readonly saving: boolean;
  readonly saved: boolean;
  readonly deletePlaylist: () => void;
  readonly deleting: boolean;
  readonly problem: ProblemCopy | null;
}

class ProblemError extends Error {
  constructor(readonly problem: ApiProblem | AuthApiProblem) {
    super('api problem');
  }
}

function problemOf(error: unknown): ApiProblem {
  if (error instanceof ProblemError) return error.problem as ApiProblem;
  return { kind: 'transport', error: { kind: 'network', status: null, url: '', retryable: true } };
}

export function usePlaylist(playlistId: string): PlaylistBinding {
  const session = useSession();
  const grantKey = session.grantedAtEpochSec ?? 0;
  /*
   * Which endpoint answers. `authPlaylistReadPreferred` sends the credential only for an `IVPL…` id,
   * because that is the only kind that can be private — and the public endpoint provably cannot be
   * talked into serving one (it 404s with the token attached exactly as without).
   */
  const authenticated = authPlaylistReadPreferred(playlistId, session.ready && session.facts.signedIn);
  // Read before the query, because it decides which end the first request asks for.
  const reversed = usePlaylistReversed(playlistId);

  const loadPage = useCallback(
    async (page: number): Promise<Playlist> => {
      const boot = await getPlatform();
      if (!boot.ok) throw new ProblemError({ kind: 'bad-url', path: `/api/v1/playlists/${playlistId}` });
      const result = authenticated
        ? // `null` scopes on a read, `useSubscriptions.ts`'s reason: the instance is the authority, and
          // a page that pre-refuses reports a scope gap it inferred rather than one that happened.
          await fetchAuthPlaylistPage(boot.platform, playlistId, page, null)
        : await fetchPlaylistPage(boot.platform, playlistId, page);
      if (!result.ok) throw new ProblemError(result.error);
      return result.value;
    },
    [authenticated, playlistId],
  );

  const pages = useInfiniteQuery({
    /*
     * The two endpoints are separate cache entries, deliberately. They answer differently for a private
     * playlist, so one key for both would let a `404` cached while signed out be served to a request
     * that would have succeeded — and the reverse, a private playlist's contents surviving a sign-out.
     */
    queryKey: authenticated ? authPlaylistKey(playlistId, grantKey) : ['playlist', playlistId],
    // One key for both directions, so flipping the order keeps every page already paid for. Which end
    // the *first* request asks for is all the direction decides here.
    initialPageParam: reversed ? PLAYLIST_HEADER_PROBE_PAGE : PLAYLIST_FIRST_PAGE,
    queryFn: ({ pageParam }) => loadPage(pageParam),
    // Reads *all* pages and every page number asked for, not only the last: which page comes next is
    // "which one holds the index the contiguous run stops at", and no single page can answer that.
    getNextPageParam: (
      _last: Playlist,
      all: readonly Playlist[],
      _lastParam: number,
      allParams: readonly number[],
    ) => playlistWalk(all, { backward: reversed, requested: allParams }).nextPage,
    retry: false,
    staleTime: 5 * 60 * 1000,
    // Until the session is known there is no answer to *which endpoint*, and guessing the public one
    // would 404 every private playlist on arrival and cache it. The instance — and therefore the
    // token's key — resolves asynchronously, so this is a real wait rather than a precaution.
    enabled: session.ready,
  });

  /* -- the writes --------------------------------------------------------------------------------- */

  const { update, remove: deleteThis, saving, deleting } = useEditPlaylist();
  const entries = useEntryMutations();

  const [open, setOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null);
  const [privacyDraft, setPrivacyDraft] = useState<PlaylistPrivacy | null>(null);
  // Shared with the Playlists page through a module store, so a playlist created there as unlisted does
  // not arrive here reading "Not public" — the app forgetting what it sent one round trip ago.
  const knownPrivacy = useKnownPlaylistPrivacy().get(playlistId) ?? null;
  const [problem, setProblem] = useState<ProblemCopy | null>(null);
  const [saved, setSaved] = useState(false);
  /**
   * Entries removed by this session, applied locally until the refetch lands.
   *
   * Not cosmetic. `withoutPlaylistEntries` renumbers, because the server does — so without it the row
   * stays on screen for a round trip, which reads as "Remove did nothing" and invites a second click,
   * whose `404` message is about a position that plainly exists. The set can safely outlive the
   * refetch: applying it to a list that has already dropped the row is a no-op by construction.
   */
  const [removed, setRemoved] = useState<readonly string[]>(EMPTY_KEYS);

  /* -- manage mode's own state — declared here because the rows carry the selection ------------- */

  const [manage, setManage] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(NO_SELECTION);
  /** The last entry toggled — shift-click's other end. A ref: it must never re-render anything. */
  const selectAnchor = useRef<string | null>(null);
  const [working, setWorking] = useState<{ done: number; total: number } | null>(null);
  const [manageProblem, setManageProblem] = useState<ProblemCopy | null>(null);
  const [addInput, setAddInput] = useState('');
  const [findState, setFindState] = useState<VideoFindState>(IDLE_FIND);
  const [addStates, setAddStates] = useState<ReadonlyMap<string, 'adding' | 'added' | 'failed'>>(
    NO_ADD_STATES,
  );
  /** Stamped per submission so a slow answer cannot overwrite a newer question's. */
  const findSeq = useRef(0);
  const [transfer, setTransfer] = useState<'copy' | 'move' | null>(null);
  // Fetched when a transfer dialog opens, never on mount — the list costs ~1.2 KB per entry across
  // the whole collection and has no summary form (`useOwnedPlaylists.ts`).
  const owned = useOwnedPlaylists(transfer !== null);

  /* -- browse-mode removal and the display order --------------------------------------------------- */

  const [search, setSearch] = useState('');
  const [removingOne, setRemovingOne] = useState<ReadonlySet<string>>(NO_SELECTION);
  const [entryProblem, setEntryProblem] = useState<ProblemCopy | null>(null);

  const first = pages.data?.pages[0] ?? null;
  const loaded = pages.data?.pages ?? null;
  /*
   * Which page number each loaded page was asked for under. They are all numbers — `initialPageParam`
   * and `getNextPageParam` are the only two sources — and `playlistWalk` falls back to counting from
   * one if this ever arrives a different length than the pages themselves.
   */
  const requestedPages = useMemo(
    () =>
      (pages.data?.pageParams ?? NO_PAGE_PARAMS).filter(
        (page): page is number => typeof page === 'number',
      ),
    [pages.data],
  );

  /**
   * The entries the reader may see, with this session's removals applied: the contiguous run from the
   * end being read, which is the far end of the playlist when the order is reversed.
   */
  const listing = useMemo(
    () =>
      loaded === null
        ? null
        : withoutPlaylistEntries(
            playlistWalk(loaded, { backward: reversed, requested: requestedPages }),
            removed,
          ),
    [loaded, removed, requestedPages, reversed],
  );

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = pages;

  /*
   * A run with nothing in it is fetched for rather than waited on. Two arrivals produce one: a
   * reversed read, whose first response is the header-only probe page, and a direction flipped over a
   * cache holding only the other end's pages. Both leave a list with no rows to scroll, so the paging
   * row's observer would never fire and the page would sit on a request it could make.
   */
  useEffect(() => {
    if (listing === null || listing.entries.length > 0) return;
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, listing]);

  const nowEpochSec = useMemo(() => {
    // Anchored to when the rows *arrived* rather than to this render — 2b's rule. With paging that
    // means the first page's arrival, so a list left open does not creep as later pages land.
    const arrivedAt = pages.dataUpdatedAt > 0 ? pages.dataUpdatedAt : systemClock.nowEpochMs();
    return Math.floor(arrivedAt / 1000);
  }, [pages.dataUpdatedAt]);

  const progress = useWatchProgressLookup();

  const state = useMemo((): PlaylistScreenState => {
    const header = loaded?.[0] ?? null;

    return playlistScreenState(
      // The header comes from the *first* page. Every page re-sends it identically, so this is a choice
      // of which copy to read rather than a fallback.
      header === null
        ? null
        : {
            ...header,
            videoCount: playlistCountAfterRemovals(
              header.videoCount,
              // Only the removals that actually applied: a key already absent must not decrement twice.
              listing === null ? 0 : appliedPlaylistRemovals(loaded, removed),
            ),
          },
      listing,
      pages.error === null ? null : problemOf(pages.error),
      {
        nowEpochSec,
        loadingMore: pages.isFetchingNextPage,
        // Editable only when the playlist is ours *and* there is a session to write with. Both, because
        // an `IVPL…` playlist read while signed out is still not one this build can change — and one
        // read while signed in as somebody else is not either, which is what the name is for.
        canEdit: session.ready && session.facts.signedIn,
        viewerUsername: session.facts.username,
        knownPrivacy,
        selected,
        progress,
        reversed,
        search,
      },
    );
  }, [
    knownPrivacy,
    listing,
    loaded,
    nowEpochSec,
    pages.error,
    pages.isFetchingNextPage,
    progress,
    removed,
    reversed,
    search,
    selected,
    session.facts.signedIn,
    session.facts.username,
    session.ready,
  ]);

  const save = useCallback(() => {
    if (first === null || saving) return;
    /*
     * **The title goes out on every save, whether or not it changed**, because `PATCH` without it is
     * `403 {"error":"Missing hash key: \"title\""}`. `updatePlaylistBody` types it as required so this
     * cannot be forgotten; what has to be decided *here* is which title — the draft if the reader
     * touched the field, otherwise the stored one, never the empty string.
     */
    const title = (titleDraft ?? first.title).trim();
    if (title === '') {
      setProblem(text().playlists.title.missingProblem);
      return;
    }

    const chosenPrivacy = privacyDraft;
    void (async () => {
      setProblem(null);
      setSaved(false);
      const result = await update(first.playlistId, {
        title,
        // Omitted rather than sent unchanged: the instance **preserves** what a PATCH leaves out
        // (measured across all three fields), so sending a description we only read back risks writing
        // a stale copy over an edit made somewhere else between the read and the save.
        ...(descriptionDraft === null ? {} : { description: descriptionDraft }),
        ...(chosenPrivacy === null ? {} : { privacy: chosenPrivacy }),
      });
      if (!result.ok) {
        setProblem(describeWriteProblem(result.problem, 'update'));
        return;
      }
      setSaved(true);
      setTitleDraft(null);
      setDescriptionDraft(null);
      setPrivacyDraft(null);
      // Now we know it exactly, which no read can tell us. Kept for the session so the panel and the
      // header stop saying "Not public" about a playlist whose privacy was just chosen.
      if (chosenPrivacy !== null) rememberPlaylistPrivacy(first.playlistId, chosenPrivacy);
    })();
  }, [descriptionDraft, first, privacyDraft, saving, titleDraft, update]);

  const deletePlaylist = useCallback(() => {
    if (first === null || deleting) return;
    void (async () => {
      setProblem(null);
      const result = await deleteThis(first.playlistId);
      if (!result.ok) {
        setProblem(describeWriteProblem(result.problem, 'delete'));
        return;
      }
      // A deleted playlist's id must not keep a remembered privacy: ids are random, but leaving the
      // entry means the store grows for the session with facts about nothing.
      forgetPlaylistPrivacy(first.playlistId);
      // The playlist page cannot survive its own playlist, so this is a navigation rather than a state
      // change. `replace`, so Back does not land on a page that is now a 404.
      navigate(formatRoute({ kind: 'feed', feed: 'playlists' }), { replace: true });
    })();
  }, [deleteThis, deleting, first]);

  /* -- the manage acts ----------------------------------------------------------------------------- */

  /**
   * One serial pass over the selection, shared by all three bulk acts — the Playlists page's shape.
   * Serial because each write is one small request against one machine, and a progress count that
   * means something beats finishing marginally sooner. The selection is cleared either way: what
   * failed is still in the refreshed list, and the notice says re-selecting it is the retry.
   */
  const runOverSelection = useCallback(
    (act: (indexId: string) => Promise<boolean>) => {
      const ids = [...selected];
      if (ids.length === 0 || working !== null) return;
      setManageProblem(null);
      setWorking({ done: 0, total: ids.length });

      void (async () => {
        let failures = 0;
        try {
          for (const [index, indexId] of ids.entries()) {
            if (!(await act(indexId))) failures += 1;
            setWorking({ done: index + 1, total: ids.length });
          }
        } finally {
          setWorking(null);
          setSelected(NO_SELECTION);
          selectAnchor.current = null;
          setTransfer(null);
          if (failures > 0) setManageProblem(text().playlist.manage.failed);
        }
      })();
    },
    [selected, working],
  );

  const removeSelected = useCallback(() => {
    if (first === null) return;
    runOverSelection(async (indexId) => {
      const result = await entries.remove(first.playlistId, indexId);
      // The server renumbers on removal, so the local list must too (`withoutPlaylistEntries`).
      if (result.ok) setRemoved((previous) => [...previous, indexId]);
      return result.ok;
    });
  }, [entries, first, runOverSelection]);

  /** One entry, straight from its card — the history page's per-row remove, for playlists. */
  const removeEntry = useCallback(
    (indexId: string) => {
      if (first === null || removingOne.has(indexId)) return;
      setEntryProblem(null);
      setRemovingOne((previous) => new Set(previous).add(indexId));
      void (async () => {
        const result = await entries.remove(first.playlistId, indexId);
        if (result.ok) setRemoved((previous) => [...previous, indexId]);
        else setEntryProblem(describeWriteProblem(result.problem, 'remove'));
        setRemovingOne((previous) => {
          const next = new Set(previous);
          next.delete(indexId);
          return next;
        });
      })();
    },
    [entries, first, removingOne],
  );

  const transferTo = useCallback(
    (targetId: string) => {
      if (first === null || transfer === null) return;
      const kind = transfer;
      // The video behind each selected key, read off the rows the reader is looking at.
      const byIndexId = new Map<string | null, string>(
        (state.kind === 'ready' ? state.rows : []).map((row) => [row.indexId, row.videoId]),
      );
      runOverSelection(async (indexId) => {
        const videoId = byIndexId.get(indexId);
        if (videoId === undefined) return false;
        // Copy first in both acts: a move must not delete what failed to arrive. A dead entry's add
        // is refused upstream ("Video unavailable"), so moving one fails visibly and removes nothing.
        const added = await entries.add(targetId, videoId);
        if (!added.ok) return false;
        if (kind === 'move') {
          const removedResult = await entries.remove(first.playlistId, indexId);
          if (!removedResult.ok) return false;
          setRemoved((previous) => [...previous, indexId]);
        }
        return true;
      });
    },
    [entries, first, runOverSelection, state, transfer],
  );

  /* -- the add box --------------------------------------------------------------------------------- */

  const find = useCallback(() => {
    const query = classifyAddVideoInput(addInput);
    if (query.kind === 'empty') {
      setFindState(IDLE_FIND);
      return;
    }

    const seq = ++findSeq.current;
    const settle = (next: VideoFindState): void => {
      if (findSeq.current === seq) setFindState(next);
    };
    setFindState({ kind: 'busy' });

    void (async () => {
      const boot = await getPlatform();
      if (!boot.ok) {
        settle({ kind: 'problem', detail: text().playlist.manage.add.notAvailable });
        return;
      }

      if (query.kind === 'video') {
        const details = await fetchVideoDetails(boot.platform, query.videoId);
        settle(
          details.ok
            ? { kind: 'results', rows: [videoCandidateFromDetails(details.value)] }
            : { kind: 'not-available' },
        );
        return;
      }

      const result = await fetchSearch(
        boot.platform,
        { ...DEFAULT_SEARCH_QUERY, q: query.q, type: 'video' },
        null,
      );
      if (!result.ok) {
        settle({ kind: 'problem', detail: text().playlist.manage.add.nothing(query.q) });
        return;
      }
      const rows = videoCandidatesFromSearch(result.value.items).slice(0, MAX_CANDIDATES);
      settle(rows.length === 0 ? { kind: 'nothing', q: query.q } : { kind: 'results', rows });
    })();
  }, [addInput]);

  const addVideo = useCallback(
    (videoId: string) => {
      if (first === null || addStates.get(videoId) === 'adding') return;
      setAddStates((previous) => new Map(previous).set(videoId, 'adding'));
      void (async () => {
        const result = await entries.add(first.playlistId, videoId);
        setAddStates((previous) => new Map(previous).set(videoId, result.ok ? 'added' : 'failed'));
      })();
    },
    [addStates, entries, first],
  );

  /** The dialog's target rows — every owned playlist but this one, with a create form beneath. */
  const targets = useMemo((): readonly TransferTarget[] | null => {
    if (transfer === null || owned.playlists === null) return null;
    return owned.playlists
      .filter((playlist) => playlist.playlistId !== playlistId)
      .map((playlist) => ({
        playlistId: playlist.playlistId,
        title: playlist.title.trim() === '' ? text().playlists.untitled : playlist.title,
        countText: fmt().countedNoun(playlist.videoCount, text().playlists.video),
      }));
  }, [owned.playlists, playlistId, transfer]);

  return {
    state,
    loadMore: () => {
      if (pages.hasNextPage && !pages.isFetchingNextPage) void pages.fetchNextPage();
    },
    retry: () => void pages.refetch(),
    refreshing: pages.isFetching && !pages.isPending,
    edit: {
      open,
      setOpen: (next: boolean) => {
        setOpen(next);
        if (!next) {
          // Closing discards the drafts rather than keeping them: a panel that reopens holding a
          // half-typed title from ten minutes ago is worse than one that reopens showing what is stored.
          setTitleDraft(null);
          setDescriptionDraft(null);
          setPrivacyDraft(null);
          setProblem(null);
          setSaved(false);
        }
      },
      title: titleDraft ?? first?.title ?? '',
      setTitle: (next: string) => {
        setTitleDraft(next);
        setSaved(false);
        if (problem !== null) setProblem(null);
      },
      description: descriptionDraft ?? first?.description ?? '',
      setDescription: (next: string) => {
        setDescriptionDraft(next);
        setSaved(false);
      },
      privacy: privacyDraft ?? knownPrivacy,
      setPrivacy: (next: PlaylistPrivacy) => {
        setPrivacyDraft(next);
        setSaved(false);
      },
      save,
      saving,
      saved,
      deletePlaylist,
      deleting,
      problem,
    },
    manage: {
      active: manage,
      toggle: useCallback(() => {
        setManage((previous) => !previous);
        // Leaving manage mode drops everything that is about the mode — the selection, the find
        // results, the failure notice, an open dialog. The list itself is untouched.
        setSelected(NO_SELECTION);
        selectAnchor.current = null;
        setManageProblem(null);
        setAddInput('');
        setFindState(IDLE_FIND);
        setAddStates(NO_ADD_STATES);
        setTransfer(null);
        findSeq.current += 1;
      }, []),
      selectedCount: selected.size,
      toggleSelect: useCallback(
        (indexId: string, range: boolean) => {
          // The displayed rows — search-filtered and possibly reversed — because a shift-range is
          // a claim about what the reader sees (`rangeToggle`'s contract). Dead entries keep their
          // `indexId` and select like any other, so only a `null` key drops out.
          const order =
            state.kind === 'ready'
              ? state.rows.flatMap((row) => (row.indexId === null ? [] : [row.indexId]))
              : [];
          // Read before `setSelected`, never inside the updater: the updater runs at flush time,
          // after the line below has already moved the anchor onto the clicked key — which made
          // every shift-range degrade to a plain toggle (measured, driven browser).
          const anchor = selectAnchor.current;
          selectAnchor.current = indexId;
          setSelected((previous) => rangeToggle(previous, order, anchor, indexId, range));
        },
        [state],
      ),
      clearSelection: useCallback(() => {
        setSelected(NO_SELECTION);
        selectAnchor.current = null;
      }, []),
      working,
      problem: manageProblem,
      addInput,
      setAddInput: useCallback((value: string) => {
        setAddInput(value);
        // Typing again after an answer invalidates the answer — the same rule the search box follows.
        findSeq.current += 1;
        setFindState(IDLE_FIND);
      }, []),
      find,
      findState,
      addVideo,
      addStates,
      removeSelected,
      transfer,
      openTransfer: useCallback((kind: 'copy' | 'move') => setTransfer(kind), []),
      closeTransfer: useCallback(() => setTransfer(null), []),
      targets,
      targetsProblem:
        transfer !== null && owned.problem !== null
          ? describeAuthProblem(owned.problem, ownedPlaylistsProblem())
          : null,
      transferTo,
    },
    reversed,
    setReversed: useCallback(
      (next: boolean) => setPlaylistReversed(playlistId, next),
      [playlistId],
    ),
    search,
    setSearch,
    removeEntry,
    removingIndexIds: removingOne,
    entryProblem,
  };
}

const EMPTY_KEYS: readonly string[] = [];
const NO_PAGE_PARAMS: readonly number[] = [];
const NO_SELECTION: ReadonlySet<string> = new Set<string>();
const NO_ADD_STATES: ReadonlyMap<string, 'adding' | 'added' | 'failed'> = new Map();
const IDLE_FIND: VideoFindState = { kind: 'idle' };

/** Enough rows to find the video meant, few enough that each Add button stays deliberate. */
const MAX_CANDIDATES = 8;

